import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, Upload } from 'lucide-react';
import type { DatasetListItem } from '../types';
import { colors, radii, transitions } from '../theme/tokens';
import { DatasetTable } from '../components/DatasetTable';
import { ListPanel } from '../components/shared/ListTable';

interface DatasetHubProps {
  datasets: DatasetListItem[];
  isLoading: boolean;
  error?: string | null;
  onOpen: (dataset: DatasetListItem) => void;
  selectedDatasetPath?: string | null;
  onUploadPDF?: () => void;
  onLinkFilevineProject?: () => void;
  onDelete?: (dataset: DatasetListItem) => void;
  deletingDatasetPath?: string | null;
  isDeleting?: boolean;
}

const HubHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const HubHeaderLeft = styled.div``;

const HubHeading = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  color: #111;
  letter-spacing: -0.02em;
`;

const HubSubtitle = styled.p`
  font-size: 13px;
  color: #999;
  margin: 4px 0 0;
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const HubContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;


const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: #111;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 24px;
  border-radius: ${radii.lg};
  border: 1px dashed ${colors.border.strong};
  background: ${colors.surface.subtle};
  font-size: 14px;
  color: ${colors.text.tertiary};
`;

const EmptyActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${radii.sm};
  border: none;
  background: linear-gradient(90deg, ${colors.accents.primary}, ${colors.accents.primaryDark});
  color: ${colors.surface.base};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background ${transitions.default};

  &:hover {
    background: linear-gradient(90deg, ${colors.accents.primaryDark}, ${colors.accents.primaryDarker});
  }
`;

const ErrorBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: ${radii.base};
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: ${colors.backgroundTones.danger};
  color: ${colors.status.danger};
  font-size: 13px;
`;

const ListSkeletonRow = styled.div`
  height: 64px;
  background: linear-gradient(110deg, rgba(255, 255, 255, 0.6), rgba(226, 232, 240, 0.7), rgba(255, 255, 255, 0.6));
  background-size: 200% 200%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% { background-position: 0% 0%; }
    50% { background-position: 100% 100%; }
    100% { background-position: 0% 0%; }
  }
`;



export const DatasetHub: React.FC<DatasetHubProps> = ({
  datasets,
  isLoading,
  error,
  onOpen,
  selectedDatasetPath,
  onUploadPDF,
  onLinkFilevineProject,
  onDelete,
  deletingDatasetPath,
  isDeleting = false,
}) => {
  return (
    <HubContainer>
      <HubHeaderRow>
        <HubHeaderLeft>
          <HubHeading>Cases</HubHeading>
          <HubSubtitle>
            {datasets.length} {datasets.length === 1 ? 'case' : 'cases'}
            {' · '}
            {datasets.reduce((sum, d) => sum + (d.pageCount ?? 0), 0).toLocaleString()} total pages
          </HubSubtitle>
        </HubHeaderLeft>
        <ControlsRow>
          {onLinkFilevineProject && (
            <SecondaryButton
              type="button"
              onClick={onLinkFilevineProject}
              aria-label="Link a Filevine project"
            >
              Link Filevine Project
            </SecondaryButton>
          )}
          {onUploadPDF && (
            <UploadButton type="button" onClick={onUploadPDF} aria-label="Upload and process PDFs">
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Upload PDFs
            </UploadButton>
          )}
        </ControlsRow>
      </HubHeaderRow>

      {error && (
        <ErrorBanner role="alert">
          <AlertTriangle size={16} /> {error}
        </ErrorBanner>
      )}

      {isLoading && !datasets.length ? (
        <ListPanel>{Array.from({ length: 4 }).map((_, index) => <ListSkeletonRow key={index} />)}</ListPanel>
      ) : datasets.length ? (
        <DatasetTable
          datasets={datasets}
          selectedDatasetPath={selectedDatasetPath}
          onOpen={onOpen}
          onDelete={onDelete}
          isDeleting={isDeleting}
          deletingDatasetPath={deletingDatasetPath}
        />
      ) : (
        <EmptyState>
          <p>No cases yet. Upload a PDF to get started.</p>
          {onUploadPDF && (
            <EmptyActionButton type="button" onClick={onUploadPDF}>
              <Upload size={16} /> Upload a PDF to create a new case
            </EmptyActionButton>
          )}
        </EmptyState>
      )}
    </HubContainer>
  );
};

export default DatasetHub;
